import type { TokenListing } from "../wire/protocol";

/**
 * What one panel row prints beside its id (#37).
 *
 * The id is NOT here because it is always printed: it is what every error
 * message, `/diagnostics` row and GitLab issue quotes, and a panel showing only
 * names would leave the user unable to answer "which workspace id?".
 */
export type RowName =
  /** The verified token lists this id under this name. */
  | { readonly kind: "named"; readonly name: string }
  /**
   * Nothing to add: the relay has not said (or predates names), or the token
   * lists the id without a name. A bare id, and not a warning — the id IS
   * listed, or nobody has said it is not.
   */
  | { readonly kind: "bare" }
  /**
   * The frame reports an id the verified token does not list. Displayed, not
   * refused: the relay does not enforce this today, so a panel that refused
   * would be claiming a protection nothing performs.
   */
  | { readonly kind: "unlisted" };

/**
 * Match a REPORTED id against the relay's VERIFIED list.
 *
 * ⚠️ An explicit miss branch, never a `.find()` read through. A miss returns
 * `undefined`, `String(undefined)` is `"undefined"`, and `textContent` prints
 * it: a failure and an empty success sharing a shape, on the surface a user
 * reads to decide whether to let an agent drive.
 *
 * ⚠️ Compared as strings. The relay stringifies the token's numeric ids and
 * `getUrlParams` reports strings; a raw comparison would miss every time.
 */
export function nameFor(reportedId: string, listings: readonly TokenListing[] | null): RowName {
  if (listings === null) return { kind: "bare" };
  const listed = listings.find((listing) => listing.id === reportedId);
  if (listed === undefined) return { kind: "unlisted" };
  return listed.name === null ? { kind: "bare" } : { kind: "named", name: listed.name };
}

/** What the panel prints beside an unlisted id. */
export const UNLISTED_NOTE = "not in your sign-in token";
